import { User } from '../../../types/shared';
import { MenuHeader } from '../../menu/menu-mobile/menu-header/menu-header';
import { StyledMenuMobile } from '../../menu/menu-mobile/menu-mobile.styles';
import { createSignal, Match, Show, Switch, useContext } from 'solid-js';
import { StyledVerticalSpace } from '../../../ui-components/utility-style-components/spacing';
import { Button } from '../../../ui-components/button/button';
import { Text } from '../../../ui-components/text/text';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { UserOrderHistory } from '../../order-history/user-order-history';
import { TabHandler } from '../../menu/menu-desktop/tab-handling';
import { StyledLoginMenuSignedInAsBoldMobile, StyledLoginMenuSignedInAsContentMobile, StyledLoginMenuSignedInAsMobile, StyledMenuHeaderLink, StyledMenuHeaderLinks, StyledNavigation } from './user-profile-menu-mobile.styles';
import { AccountEducationContent } from '../../account-education-content/account-education-content';
import { MyContent } from '../../my-content/my-content';
import { SmallCertificateIcon } from '../../icons-library/small-certificate';
import { StarIcon } from '../../icons-library/star';
import { useNavigate } from '@solidjs/router';
import { ArrowRightIcon } from '../../icons-library/arrow-right';
import theme from '../../../style/theme';

type UserProfileMenuMobileProps = {
    user: User | null;
    handleCloseMenuClick: () => void;
    handleSignOutClick: () => void;
};

export const UserProfileMenuMobile = (props: UserProfileMenuMobileProps) => {
    const { 
        localize,
        siteInfo,
        coursesListingPage,
        pdfBuilderPage,
        translations,
    } = useContext(AppContext);

    const navigate = useNavigate();

    const [ activeView, setActiveView ] = createSignal(siteInfo.siteType === 'atos-care'
        ? 'orders'
        : coursesListingPage.url ? 'learning' : 'bookmarks'
    );

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const Navigation = () => (
        <StyledNavigation>
            <Text fontSize='small' displayRedVerticalLine={false} noBlockSpacing>
                {`${localize('my-content', 'My content')}:`}
            </Text>

            <StyledMenuHeaderLinks>
                <Show when={coursesListingPage.url}>
                    <StyledMenuHeaderLink
                        onClick={() => setActiveView('learning')}
                        active={activeView() === 'learning'}
                        aria-label={localize('navigate-to-e-learning', 'Navigate to E-learning')}
                    >
                        <SmallCertificateIcon width={1.75} height={1.75} fill={activeView() === 'learning' ? theme.palette.red : theme.colors.svg.default} />
                        <span>{localize('e-learning', 'E-learning')}</span>
                    </StyledMenuHeaderLink>
                </Show>
                <StyledMenuHeaderLink
                    onClick={() => setActiveView('bookmarks')}
                    active={activeView() === 'bookmarks'}
                    aria-label={localize('navigate-to-bookmarks', 'Navigate to Bookmarks')}
                >
                    <StarIcon width={1.75} height={1.75} fill={activeView() === 'bookmarks' ? theme.palette.red : theme.colors.svg.default} ariaHidden />
                    <span>{localize('bookmarks', 'Bookmarks')}</span>
                </StyledMenuHeaderLink>
            </StyledMenuHeaderLinks>

            <Show when={pdfBuilderPage.url}>
                <Text fontSize='small' displayRedVerticalLine={false} noBlockSpacing>
                    {`${localize('profile-tools', 'Tools')}:`}
                </Text>

                <StyledMenuHeaderLinks>
                    <StyledMenuHeaderLink
                        onClick={() => {
                            navigate(pdfBuilderPage.url);
                            props.handleCloseMenuClick();
                        }}
                        aria-label={localize('navigate-to-pdf-builder', 'Navigate to PDF Builder')}
                    >
                        <ArrowRightIcon width={1.3} height={1.3} />
                        <span>{translations['profile-pdf-builder-heading'] || pdfBuilderPage.title}</span>
                    </StyledMenuHeaderLink>
                </StyledMenuHeaderLinks>
            </Show>
        </StyledNavigation>
    );


    return (
        <TabHandler
            refs={{
                firstTabbableElement,
                lastTabbableElement,
            }}
        >
            <StyledMenuMobile>

                <MenuHeader
                    handleCloseMenuClick={props.handleCloseMenuClick}
                    firstElement={(el: HTMLElement) => {
                        setFirstTabbableElement(el);
                    }}
                />

                <StyledLoginMenuSignedInAsContentMobile>
                    <StyledLoginMenuSignedInAsMobile>
                        <StyledLoginMenuSignedInAsBoldMobile>
                            {localize('signed-in-as', 'Signed in as')}
                        </StyledLoginMenuSignedInAsBoldMobile>
                        <div>{props.user?.firstName || props.user?.email || ''}</div>
                    </StyledLoginMenuSignedInAsMobile>
                    <StyledVerticalSpace size={1} />
                    <Button
                        label={localize('sign-out', 'Sign out')}
                        onClick={() => props.handleSignOutClick()}
                        small
                    />
                </StyledLoginMenuSignedInAsContentMobile>

                <Show when={siteInfo.siteType === 'default'}>
                    <Navigation />
                </Show>

                <Switch>
                    <Match when={activeView() === 'bookmarks'}>
                        <MyContent lastElement={(el: HTMLElement) => setLastTabbableElement(el)} />
                    </Match>
                    <Match when={activeView() === 'learning'}>
                        <AccountEducationContent lastElement={(el: HTMLElement) => setLastTabbableElement(el)} />
                    </Match>
                    <Match when={activeView() === 'orders'}>
                        <UserOrderHistory lastElement={(el: HTMLElement) => setLastTabbableElement(el)} />
                    </Match>
                </Switch>
            </StyledMenuMobile>
        </TabHandler>
    );
};
